"use client";
import {
    Box, Button, Modal, ModalDialog, Stack, Typography, Chip, Sheet, Grid,
} from "@mui/joy";
import { useState } from "react";
import { Lock } from "@phosphor-icons/react";
import { APICreateTgPromoInvoice, APICreateStandardInvoice } from "../../api";

/**
 * Shared modal shown when a user hits a daily/paywall limit.
 *
 * Variants:
 *  - 'paywall' (default): true free-tier paywall — show payment plans
 *  - 'quota': user has a custom/admin-set daily limit (e.g. 25/day boost) and
 *    hit it — just inform them to come back tomorrow, NO payment push
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - message: string (banner subtitle text)
 *  - token: JWT
 *  - variant: 'paywall' | 'quota'
 */
export default function LimitReachedModal({ open, onClose, message, token, variant = 'paywall' }) {
    const [paymentLoading, setPaymentLoading] = useState(false);
    const isPaywall = variant === 'paywall';

    const handlePayment = async (period, isPromo) => {
        setPaymentLoading(true);
        try {
            const res = isPromo
                ? await APICreateTgPromoInvoice(token, period)
                : await APICreateStandardInvoice(token, period);
            if (res.payment_url) window.location.href = res.payment_url;
        } catch (e) {
            const msg = e?.response?.data?.error || e.message || 'Ошибка';
            alert('Ошибка: ' + msg);
        } finally {
            setPaymentLoading(false);
        }
    };

    const title = isPaywall
        ? 'Лимит бесплатного тарифа исчерпан'
        : 'Дневной лимит исчерпан';
    const fallbackMessage = isPaywall
        ? 'Ты использовал дневной лимит вопросов на бесплатном тарифе.'
        : 'Возвращайся завтра — лимит обнулится в полночь.';

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 520, borderRadius: 'xl', p: 0, overflow: 'hidden' }}>
                <Box sx={{
                    p: 3, pb: 2,
                    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                    color: '#fff', textAlign: 'center',
                }}>
                    <Box sx={{
                        width: 56, height: 56, borderRadius: '50%',
                        mx: 'auto', mb: 1.5,
                        background: 'linear-gradient(135deg, #E8511A, #FF7043)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Lock size={28} color="#fff" />
                    </Box>
                    <Typography level="h4" sx={{ color: '#fff', mb: 0.5 }}>
                        {title}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {message || fallbackMessage}
                    </Typography>
                </Box>
                {isPaywall ? (
                    <Box sx={{ p: 3 }}>
                        <Typography level="body-sm" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                            Оформи доступ и решай без ограничений
                        </Typography>
                        <Grid container spacing={1.5}>
                            {[
                                { months: 1, price: '2 500', label: '1 месяц' },
                                { months: 3, price: '4 990', label: '3 месяца', perMonth: '1 663 ₽/мес' },
                                { months: 6, price: '8 990', label: '6 месяцев', perMonth: '1 498 ₽/мес', best: true },
                            ].map((plan) => (
                                <Grid xs={12} key={plan.months}>
                                    <Sheet sx={{
                                        p: 2, borderRadius: 'lg',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        border: plan.best ? '2px solid #DA7756' : '1px solid',
                                        borderColor: plan.best ? '#DA7756' : 'divider',
                                        background: plan.best
                                            ? (theme) => theme.palette.mode === 'dark' ? 'rgba(218,119,86,0.08)' : 'rgba(218,119,86,0.04)'
                                            : undefined,
                                    }}>
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography level="title-sm">{plan.label}</Typography>
                                                {plan.best && (
                                                    <Chip size="sm" sx={{ bgcolor: '#DA7756', color: '#fff', fontSize: '0.6rem' }}>
                                                        Выгоднее
                                                    </Chip>
                                                )}
                                            </Stack>
                                            {plan.perMonth && (
                                                <Typography level="body-xs" color="neutral">{plan.perMonth}</Typography>
                                            )}
                                        </Box>
                                        <Button
                                            size="sm"
                                            loading={paymentLoading}
                                            onClick={() => handlePayment(plan.months, false)}
                                            sx={{
                                                bgcolor: '#DA7756', color: '#fff',
                                                '&:hover': { bgcolor: '#c4633f' },
                                                minWidth: 100,
                                            }}
                                        >
                                            {plan.price} ₽
                                        </Button>
                                    </Sheet>
                                </Grid>
                            ))}
                        </Grid>
                        <Button
                            fullWidth variant="plain" color="neutral" size="sm"
                            sx={{ mt: 2 }}
                            onClick={onClose}
                        >
                            Позже
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                            Лимит обнулится завтра в полночь.
                        </Typography>
                        <Button
                            fullWidth
                            onClick={onClose}
                            sx={{
                                bgcolor: '#DA7756', color: '#fff',
                                '&:hover': { bgcolor: '#c4633f' },
                            }}
                        >
                            Понятно
                        </Button>
                    </Box>
                )}
            </ModalDialog>
        </Modal>
    );
}
